import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { chatKeys } from '~/lib/query-keys';
import { usePaginatedList } from '~/hooks/use-paginated-list';

/**
 * A user-facing chat thread (FRONT-521/522). One row per `web:<uuid>` chat.
 * `resolution` and the lifecycle clocks are user-driven (FRONT-523 owns the
 * resolution bar); FRONT-518 only reads `sessionId`, `title`, and `state`.
 */
export interface ChatThreadRow {
  id: string;
  sessionId: string;
  title: string | null;
  state: string;
  resolution: string | null;
  documentFilename: string | null;
  messageCount: number;
  assistantTurnCount: number;
  lastActiveAt: string | null;
  createdAt: string;
  updatedAt: string;
  trashedAt: string | null;
  materializedAt: string | null;
  resolvedAt: string | null;
  /** FRONT-1309: when this chat became kept (by a user resolution or a
   *  system materialize), or null if never kept — the one fact "is this
   *  kept" queries read, independent of `state`/liveness. */
  keptAt: string | null;
  /** FRONT-2433: when the user pinned this chat to the top of the Chats
   *  page, or null. Optional: only the thread-row reads added it. */
  pinnedAt?: string | null;
  /** FRONT-593: names of files the user uploaded into this chat. Empty when
   *  none. Shown on the Chats-overview row so attached docs are spottable. */
  attachmentNames?: string[];
  /**
   * FRONT-1738: the Work container (a project) this chat is filed under, if
   * any — populated ONLY by `GET /api/chats` (the row-level projection every
   * other read of a `ChatThreadRow` doesn't carry this). `entityId`, not
   * `id`: a distinct wire shape from `ChatScopeEntity` (`use-chat-scope.ts`),
   * which backs the single-chat scope route instead.
   */
  scope?: { entityId: string; name: string; type: 'project'; archived: boolean } | null;
  /** FRONT-2522: the agent this chat is pinned to (direct agent chat). Sent
   *  by the list, search and single-chat reads; null/absent for an ordinary
   *  chat. */
  directAgent?: { id: string; name: string } | null;
}

function isAuthError(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 401;
}

/**
 * Every non-trashed, non-purged lifecycle state — the default for a
 * container's Chats panel (FRONT-1738): a filed chat should stay listed
 * there through its whole lifecycle short of being thrown away.
 *
 * FRONT-1309: this used to also spread in `KEPT_STATES`
 * (`resolved_document`/`resolved_graph`/`materialized`) because a saved chat
 * used to change its OWN `state` to one of those — without including them
 * here, a chat filed under a project and then saved as a document would have
 * silently dropped off the panel. Keptness is a separate `keptAt` fact now,
 * orthogonal to `state`, so a kept chat's `state` stays `active`/`archived`
 * like any other and no longer needs special-casing here at all.
 */
const CONTAINER_CHAT_DEFAULT_STATES = ['active', 'archived'];

/**
 * The recent active chats — the launcher pill row, the top bar's activity
 * board, and (FRONT-1579) the modal's own "Chat activity" dropdown all read
 * this (FRONT-518). Newest-active first, capped at `limit` (the spec's "last ~10").
 * Freshness comes from invalidation on send/close/archive (see the launcher);
 * the modest poll is a fallback so a thread minted server-side on the first
 * message, or its title (named from the first message at send), surfaces
 * without a manual refresh.
 */
export function useActiveChats(limit = 10) {
  return useQuery({
    queryKey: chatKeys.threads('active', limit),
    queryFn: async () => {
      const data = await api.get<{ threads: ChatThreadRow[] }>(
        `/api/chats?state=active&limit=${limit}`,
      );
      return data.threads;
    },
    refetchInterval: (q) => (isAuthError(q.state.error) ? false : 15_000),
    retry: (_count, err) => !isAuthError(err),
  });
}

/**
 * FRONT-3152 / FRONT-1804: how often to re-read one chat's row. A new chat is
 * named ~3 s after Send, while its reply streams, and the server pushes
 * `chat-title-changed` when it writes the name — but that push only reaches
 * this tab when its live connection is on the replica that minted (the event
 * bus is per process). So the window whose OWN turn went live at
 * `turnLiveSince` (ms; null = not live) also polls its row from Postgres every
 * `CHAT_TITLE_LIVE_POLL_MS`, for at most `CHAT_TITLE_LIVE_WINDOW_MS` after that
 * moment, while the row is missing (nothing minted yet) or untitled. The live
 * branch does not look at the row's state: sending revives a chat, but the
 * cache can still hold a stale archived/trashed copy. Outside the live branch
 * the older window applies: a minted, still-untitled, ACTIVE row whose last
 * activity is under a minute old, every `CHAT_TITLE_POLL_MS`. A titled row is
 * never polled; a mint that failed costs at most the 30 s of 2 s reads while
 * the turn is live, then nothing. Exported for tests.
 */
export const CHAT_TITLE_POLL_MS = 4_000;
export const CHAT_TITLE_LIVE_POLL_MS = 2_000;
export const CHAT_TITLE_LIVE_WINDOW_MS = 30_000;
const CHAT_TITLE_POLL_WINDOW_MS = 60_000;

export function chatThreadPollInterval(
  row: ChatThreadRow | null | undefined,
  error: unknown,
  now: number = Date.now(),
  turnLiveSince: number | null = null,
): number | false {
  if (isAuthError(error)) return false;
  if (
    turnLiveSince !== null &&
    now - turnLiveSince < CHAT_TITLE_LIVE_WINDOW_MS &&
    (!row || row.title === null)
  ) {
    return CHAT_TITLE_LIVE_POLL_MS;
  }
  if (!row || row.title !== null || row.state !== 'active') return false;
  const last = Date.parse(row.lastActiveAt ?? row.createdAt);
  if (!Number.isFinite(last) || now - last > CHAT_TITLE_POLL_WINDOW_MS) return false;
  return CHAT_TITLE_POLL_MS;
}

/**
 * True when a single-row read sees a chat's name ARRIVE: the previously cached
 * row was missing (`null`, a 404 — nothing minted yet) or untitled, and the
 * fetched one is titled. `undefined` means "never read" — the first read of a
 * chat that already has its name (opening an existing chat) — and is NOT an
 * arrival, or every chat opened would refresh every chat list. Exported for
 * tests.
 */
export function titleJustArrived(
  prev: ChatThreadRow | null | undefined,
  next: ChatThreadRow | null | undefined,
): boolean {
  if (prev === undefined || !next || next.title === null) return false;
  return prev === null || prev.title === null;
}

const SINGLE_THREAD_PREFIX = JSON.stringify(chatKeys.thread('').slice(0, -1)).slice(0, -1);

/** A `chatKeys.thread(id)` key — one chat's row, not a list. */
function isSingleThreadKey(key: readonly unknown[]): boolean {
  return JSON.stringify(key).startsWith(SINGLE_THREAD_PREFIX + ',');
}

/**
 * One chat's lifecycle row by session id (FRONT: trashed-chat modal cue). Backs
 * the modal's "In Recycle bin" badge + Restore/Trash swap. 404s (a brand-new
 * chat whose thread isn't minted yet) resolve to null — the modal then treats it
 * as a normal live chat. Disabled for empty ids so the launcher's blank state
 * makes no request.
 *
 * `turnLiveSince`: when this window's own turn went live (null = not live) — see
 * `chatThreadPollInterval`. When a read sees the title arrive, the list-shaped
 * chat keys (switcher, Chats timeline, search…) are refreshed too (the command
 * palette's `recentsKeys` list is not under `threadsRoot()`; it refetches when
 * the palette opens), so a replica
 * that missed the `chat-title-changed` push does not leave them behind the
 * header; this chat's own single-row key is skipped, so nothing loops.
 */
export function useChatThread(sessionId: string | null | undefined, opts?: { turnLiveSince?: number | null }) {
  const queryClient = useQueryClient();
  const turnLiveSince = opts?.turnLiveSince ?? null;
  return useQuery({
    queryKey: chatKeys.thread(sessionId ?? ''),
    queryFn: async () => {
      const id = sessionId as string;
      const prev = queryClient.getQueryData<ChatThreadRow | null>(chatKeys.thread(id));
      let next: ChatThreadRow | null;
      try {
        const data = await api.get<{ thread: ChatThreadRow }>(`/api/chats/${encodeURIComponent(id)}`);
        next = data.thread;
      } catch (err) {
        if ((err as { status?: number } | null)?.status === 404) return null;
        throw err;
      }
      if (titleJustArrived(prev, next)) {
        void queryClient.invalidateQueries({
          queryKey: chatKeys.threadsRoot(),
          // Lists only: skip every single-chat row (this one and any other
          // open window's), which the push or their own reads keep current.
          predicate: (q) => !isSingleThreadKey(q.queryKey),
        });
      }
      return next;
    },
    enabled: Boolean(sessionId),
    refetchInterval: (q) => chatThreadPollInterval(q.state.data, q.state.error, Date.now(), turnLiveSince),
    retry: (_count, err) => !isAuthError(err),
  });
}

/** A search hit: a thread plus the highlighted snippet of its best message. */
export interface ChatSearchHit extends ChatThreadRow {
  /** ts_headline fragment (may contain `<b>` tags) from the matching message. */
  snippet?: string;
  /** FRONT-2433: whether the search hit the chat's title or a message. */
  matchedIn?: 'title' | 'message';
}

/**
 * List the user's chats by lifecycle state and/or keptness (FRONT-527 Chats
 * section; `kept` added FRONT-1309). The Open tab passes
 * `{states:['active','archived'], kept:false}` — kept chats never clutter
 * Open, regardless of liveness; the Kept tab passes `{kept:true}` with no
 * state filter — a kept chat can be `active` or `archived`, live or not; the
 * Recycle bin passes `{states:['trashed']}`. Most-recently-active first.
 */
export interface ChatListOptions {
  states?: string[];
  kept?: boolean;
  /** FRONT-2433: only pinned chats (the Chats page's pinned strip). */
  pinned?: boolean;
}

/**
 * The request params for a list read and the cache-key part that names it —
 * built from ONE normalized value so a cache entry can never describe a
 * different request than the one it holds (FRONT-1501). PURE, exported for
 * the key test.
 */
export function chatListRequest(opts: ChatListOptions, limit: number): { key: string; params: URLSearchParams } {
  const stateParam = (opts.states ?? []).join(',');
  const keptParam = opts.kept === undefined ? '' : String(opts.kept);
  const params = new URLSearchParams({ limit: String(limit) });
  if (stateParam) params.set('state', stateParam);
  if (keptParam) params.set('kept', keptParam);
  if (opts.pinned) params.set('pinned', 'true');
  return { key: `${stateParam}|kept:${keptParam}${opts.pinned ? '|pinned' : ''}`, params };
}

/** A failed list read retries a few times, then shows its error — never an
 *  endless skeleton (pr-panel finding, FRONT-2433). A 401 never retries. */
function retryBounded(count: number, err: unknown): boolean {
  return count < 3 && !isAuthError(err);
}

export function useChatList(opts: ChatListOptions, limit = 100) {
  const { key, params } = chatListRequest(opts, limit);
  return useQuery({
    queryKey: chatKeys.threads(key, limit),
    queryFn: async () => {
      const data = await api.get<{ threads: ChatThreadRow[] }>(`/api/chats?${params}`);
      return data.threads;
    },
    retry: retryBounded,
  });
}

/** FRONT-2433: the All / Open / Kept counts the timeline's first page carries. */
export interface ChatTimelineCounts {
  all: number;
  open: number;
  kept: number;
}

/**
 * FRONT-2433: the request a Chats page timeline read sends for one filter and
 * sort — live chats only, pinned chats left out (they have their own strip),
 * keptness from the filter. PURE, exported for the key/param test.
 */
export function chatTimelineParams(
  filter: 'all' | 'open' | 'kept',
  sort: 'active' | 'created',
): Record<string, string | undefined> {
  return {
    paged: '1',
    sort,
    pinned: 'false',
    kept: filter === 'all' ? undefined : String(filter === 'kept'),
  };
}

/**
 * The Chats page timeline (FRONT-2433): live chats, 50 at a time, fetched
 * further as the person scrolls — the same shared paginated list Knowledge,
 * Slides and Sketches load through. The first page carries the true
 * All / Open / Kept counts.
 */
export function useChatTimeline(filter: 'all' | 'open' | 'kept', sort: 'active' | 'created') {
  return usePaginatedList<ChatThreadRow, { counts: ChatTimelineCounts }>(
    chatKeys.timeline(filter, sort),
    '/api/chats',
    { pageSize: 50, params: chatTimelineParams(filter, sort) },
  );
}

/**
 * The chats filed under one Work container (FRONT-1738 — the project detail
 * page's own Chats panel). Same shape as `useChatList` but scoped by
 * `containerId` server-side via `GET /api/chats?containerId=…`; defaults to
 * `CONTAINER_CHAT_DEFAULT_STATES` — every non-trashed, non-purged state, so
 * a chat that was filed and later kept (saved as a document / to the Graph,
 * or auto-materialized) STAYS in the panel instead of vanishing the moment
 * it stops being "just a conversation" (the document it produced still sits
 * right below it on the same container's Files panel). Disabled for an
 * empty container id so a not-yet-loaded detail page makes no request;
 * `opts.enabled` additionally lets a caller suppress the fetch even with a
 * real id — `work-detail.tsx` uses it for the Unsorted holding container,
 * whose layout never renders the Chats panel this backs.
 */
export function useChatsForContainer(
  containerId: string | null | undefined,
  states: string[] = CONTAINER_CHAT_DEFAULT_STATES,
  limit = 100,
  opts?: { enabled?: boolean },
) {
  const stateParam = states.join(',');
  return useQuery({
    queryKey: chatKeys.byContainer(containerId ?? '', stateParam, limit),
    queryFn: async () => {
      const data = await api.get<{ threads: ChatThreadRow[] }>(
        `/api/chats?containerId=${encodeURIComponent(containerId as string)}&state=${encodeURIComponent(stateParam)}&limit=${limit}`,
      );
      return data.threads;
    },
    enabled: Boolean(containerId) && (opts?.enabled ?? true),
    retry: (_count, err) => !isAuthError(err),
  });
}

/**
 * Full-text search the user's chats (FRONT-527). Runs the same Postgres FTS the
 * agent uses — matches inside the conversation, not just titles. Disabled until
 * the query is non-empty so an idle search box makes no request.
 */
export function useChatSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: chatKeys.search(q),
    queryFn: async () => {
      const data = await api.get<{ threads: ChatSearchHit[] }>(
        `/api/chats?q=${encodeURIComponent(q)}`,
      );
      return data.threads;
    },
    enabled: q.length > 0,
    retry: retryBounded,
  });
}

/**
 * Pure: build the {documentFilename → sessionId} map that backs the Files
 * "From chat" badge. Only threads that actually produced a document (resolved
 * or auto-materialized) carry a backlink, so the rest are skipped. Exported for
 * unit testing without a live query.
 */
export function buildBacklinkMap(threads: ChatThreadRow[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const t of threads) {
    if (t.documentFilename) map.set(t.documentFilename, t.sessionId);
  }
  return map;
}

/**
 * The Files "From chat" backlink map (FRONT-527). A document shows a "From chat"
 * badge when its filename is the backlink target of a resolved/materialized
 * chat; clicking it reopens that chat. Populated by the resolution bar's "Save
 * as Document" (FRONT-523); the lifetime sweep (FRONT-526) adds materialized ones.
 *
 * FRONT-1309: fetches every LIVE kept chat (`state=active,archived&kept=true`)
 * rather than `state=resolved_document,materialized` — a graph-kept chat has
 * no `documentFilename` and is harmlessly dropped by {@link buildBacklinkMap}'s
 * own filter, same as before. `trashed` is excluded on purpose (matching the
 * Kept tab, pr-panel finding): reopening a binned chat from a Files badge
 * would be confusing, and pre-FRONT-1309 a trashed chat was never one of the
 * two states this query asked for either.
 */
export function useChatBacklinks() {
  return useQuery({
    queryKey: chatKeys.backlinks(),
    queryFn: async () => {
      const data = await api.get<{ threads: ChatThreadRow[] }>(
        '/api/chats?state=active,archived&kept=true&limit=200',
      );
      return buildBacklinkMap(data.threads);
    },
    retry: (_count, err) => !isAuthError(err),
  });
}
